<script lang="ts">
/**
 * Variants this tag can render — its public option set. As with
 * GaiaStatusChip's tones, the component never interprets backend values;
 * adapters (statusMaps.ts) translate API enums into one of these.
 */
export type GaiaTagVariant = 'default' | 'muted' | 'accent' | 'label' | 'amber';
</script>

<script setup lang="ts">
import { computed, type PropType } from 'vue';
import { tagProps } from 'naive-ui';
import {
  gaiaAmber,
  gaiaFontSize,
  gaiaFontWeight,
  gaiaAccent,
  gaiaNeutral,
  gaiaTracking,
} from '../themes/tokens';

const props = defineProps({
  ...tagProps,
  /** default = the contract-type chip (600 weight); muted = the lighter tag
      chips; accent = the violet "Generated" / count chips (Playbooks mock);
      label = the entity table's uppercase type chip; amber = a restriction
      worth noticing but not an error (a view that is Private). */
  variant: {
    type: String as PropType<GaiaTagVariant>,
    default: 'default',
  },
  size: {
    type: String as PropType<'tiny' | 'small' | 'medium' | 'large'>,
    default: 'small',
  },
  /**
   * Fully rounded and taller — the multi-select answer chips on the sign-up
   * profile step, where a chip is a thing you press rather than a label you
   * read. Composes with `checkable`.
   */
  pill: {
    type: Boolean,
    default: false,
  },
  /**
   * Clip the label to one line with an ellipsis instead of letting it run past
   * the chip's border. Same idiom as GaiaText's `truncate`: the chip shrinks to
   * whatever room its container leaves, so the cap is the column, not a number.
   */
  truncate: Boolean,
  /**
   * Let the label break onto further lines instead — for a chip whose whole
   * text matters and whose column is narrow (the intake side panel's matters).
   * The chip grows in height; `truncate` wins if both are set.
   */
  wrap: Boolean,
});

// `variant` is ours, not n-tag's — keep it off the DOM element.
const passThrough = computed(() => {
  const { variant, pill, truncate, wrap, ...rest } = props;
  return rest;
});

// Base chrome comes from the Tag theme section; the muted tint and the
// checkable border are per-variant, so they merge here (caller wins).
const variantOverrides = {
  muted: { border: `1px solid ${gaiaNeutral.borderSoft}`, textColor: gaiaNeutral.textTertiary },
  accent: {
    border: `1px solid ${gaiaAccent.border}`,
    textColor: gaiaAccent.base,
    fontWeightStrong: '600',
  },
  default: { fontWeightStrong: '600' },
  // Tinted rather than outlined: on a dense row the amber wash is what carries
  // the meaning, so the chip reads at a glance without a dot or an icon.
  amber: {
    border: `1px solid ${gaiaAmber.border}`,
    color: gaiaAmber.surface,
    textColor: gaiaAmber.strong,
    fontWeightStrong: '600',
  },
  // The caps chip is shorter and tighter than a normal tag; the uppercasing
  // and tracking have no n-tag theme vars, so they arrive as CSS below.
  label: {
    border: `1px solid ${gaiaNeutral.border}`,
    textColor: gaiaNeutral.text,
    heightSmall: '18px',
    padding: '0 6px',
    borderRadius: '5px',
    fontSizeSmall: gaiaFontSize.nano,
  },
} as const;

// The pill's radius and height are Tag theme vars, so they merge here rather
// than fighting naive's own rules from a stylesheet.
const pillOverrides = {
  borderRadius: '999px',
  heightSmall: '28px',
  heightMedium: '31px',
  padding: '0 12px',
} as const;

const mergedThemeOverrides = computed(() => ({
  ...variantOverrides[props.variant],
  ...(props.pill ? pillOverrides : {}),
  ...props.themeOverrides,
}));

const border = gaiaNeutral.border;
const borderHover = gaiaNeutral.borderHover;
const ink = gaiaNeutral.ink;
const semibold = String(gaiaFontWeight.semibold);
const bold = String(gaiaFontWeight.bold);
const labelTracking = gaiaTracking.labelTight;
</script>

<template>
  <!-- Static table chips (type/tags) and checkable permission chips from the
       CM mocks — a themed n-tag. Checkable chips get a check icon when on. -->
  <n-tag
    v-bind="passThrough"
    :strong="props.variant !== 'muted'"
    :theme-overrides="mergedThemeOverrides"
    :class="{
      'gaia-tag--checkable': props.checkable,
      'gaia-tag--label': props.variant === 'label',
      'gaia-tag--truncate': props.truncate,
      'gaia-tag--wrap': props.wrap && !props.truncate,
    }"
  >
    <template v-if="$slots.icon" #icon>
      <slot name="icon" />
    </template>
    <template v-else-if="props.checkable && props.checked" #icon>
      <svg
        width="10"
        height="10"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="3.4"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M4 12.5 9.5 18 20 6.5" />
      </svg>
    </template>
    <slot />
  </n-tag>
</template>

<style scoped>
/* n-tag has no theme var for casing or tracking, so the caps chip's two
   defining properties have to be CSS. */
.gaia-tag--label {
  text-transform: uppercase;
  letter-spacing: v-bind(labelTracking);
  font-weight: v-bind(bold);
}

/* A tag whose text can outrun its column. `min-width: 0` on both the chip and
   naive's content span is what lets them shrink below the label's own width —
   without it the flex items refuse to give ground and the text spills out. */
.gaia-tag--truncate {
  min-width: 0;
  max-width: 100%;
}

.gaia-tag--truncate :deep(.n-tag__content) {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* A tag whose text wraps: naive fixes the height to one line, so it becomes a
   floor and the chip grows with its label. */
.gaia-tag--wrap {
  max-width: 100%;
  height: auto;
  min-height: var(--n-height);
  padding-top: 2px;
  padding-bottom: 2px;
}

.gaia-tag--wrap :deep(.n-tag__content) {
  min-width: 0;
  white-space: normal;
  overflow-wrap: anywhere;
  line-height: 1.4;
}

/* naive's checkable tags are borderless by design — the mock's permission
   chips carry the control hairline, so it comes back here. */
.gaia-tag--checkable {
  border: 1px solid v-bind(border);
  font-weight: v-bind(semibold);
}

.gaia-tag--checkable:hover {
  border-color: v-bind(borderHover);
}

.gaia-tag--checkable.n-tag--checked {
  border-color: v-bind(ink);
}
</style>
